import React, { useEffect, useState } from 'react'
import './filestab.scss'
import { useWorkspace } from '../WorkspaceContext'
import { useAppUser } from '../../../context/UserContext/UserContext'
import { getFileRefIcon, getSnippetIcon } from '../../../shared/utils/getFileIconAndExtensionHelper'
import { codeOptions } from '../snippets/AcceptedLanguages'
import { MAX_ROOM_ITEMS, FOLDER_LIMIT, ROOM_STORAGE_LIMIT_BYTES, getFolderPaths } from './folderUploadLimits'

export type FileLimitPopupMode = 'file' | 'folder';

type FileLimitPopupProps = {
    files: File[];
    mode: FileLimitPopupMode;
    onConfirm: (files: File[]) => void;
    onCancel: () => void;
};

function shortenFileName(name: string) {
    if (name.length <= 30) return name;

    const dot = name.lastIndexOf('.');
    const ext = dot !== -1 ? name.slice(dot) : '';
    const base = dot !== -1 ? name.slice(0, dot) : name;

    return `${base.slice(0, 24)}...${ext}`;
}

function formatBytes(bytes: number) {
    const abs = Math.max(0, bytes);
    if (abs < 1024 * 1024) {
        return `${Math.max(1, Math.ceil(abs / 1024))} KB`;
    }

    const mb = abs / (1024 * 1024);
    return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

function getExtension(fileName: string) {
    const dot = fileName.lastIndexOf('.');
    return dot !== -1 ? fileName.slice(dot).toLowerCase() : '';
}

function getPreviewIconSrc(file: File) {
    const ext = getExtension(file.name);
    const codeMatch = codeOptions.find(o => o.extension === ext);

    if (codeMatch) {
        // Mirrors how uploaded code files are stored (uploadSnippet sets
        // snippetLanguage from cValue, not name), so the preview icon matches
        // what the file will actually show once it lands as a snippet.
        return `${process.env.PUBLIC_URL}/images/${getSnippetIcon(codeMatch.cValue)}.png`;
    }

    if (ext === '.txt') {
        return `${process.env.PUBLIC_URL}/images/${getSnippetIcon('text')}.png`;
    }

    return `${process.env.PUBLIC_URL}/images/${getFileRefIcon(file.name)}`;
}

const FileLimitPopup = ({ files, mode, onConfirm, onCancel }: FileLimitPopupProps) => {
    const { Snippets, Files, Folders, roomStorage } = useWorkspace();
    const { user: appUser } = useAppUser();
    const hasOwnerBypass = appUser?.plan === 'flashroom-owner';

    const [currentFiles, setCurrentFiles] = useState<File[]>(files);
    const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});

    useEffect(() => {
        const urls: Record<number, string> = {};

        currentFiles.forEach((file, index) => {
            if (file.type?.startsWith('image/')) {
                urls[index] = URL.createObjectURL(file);
            }
        });

        setPreviewUrls(urls);

        return () => {
            Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
        };
    }, [currentFiles]);

    const removeFileAt = (index: number) => {
        const next = currentFiles.filter((_, i) => i !== index);

        if (next.length === 0) {
            onCancel();
            return;
        }

        setCurrentFiles(next);
    };

    const totalBytes = currentFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSizeLabel = totalBytes < 1024 * 1024
        ? `${Math.max(1, Math.floor(totalBytes / 1024))} KB`
        : `${Math.floor(totalBytes / (1024 * 1024))} MB`;

    const itemsOverBy = Snippets.length + Files.length + currentFiles.length - MAX_ROOM_ITEMS;
    const exceedsItemLimit = !hasOwnerBypass && itemsOverBy > 0;
    const exceedsStorage = !hasOwnerBypass && roomStorage + totalBytes > ROOM_STORAGE_LIMIT_BYTES;
    const foldersOverBy = mode === 'folder'
        ? getFolderPaths(currentFiles).length + Folders.length - FOLDER_LIMIT
        : 0;
    const exceedsFolderLimit = !hasOwnerBypass && mode === 'folder' && foldersOverBy > 0;
    const canConfirm = !exceedsItemLimit && !exceedsStorage && !exceedsFolderLimit;

    const storageOverBy = roomStorage + totalBytes - ROOM_STORAGE_LIMIT_BYTES;

    let subtext: string;
    if (exceedsItemLimit) {
        subtext = `This batch would put you ${itemsOverBy} over the room's ${MAX_ROOM_ITEMS}-item limit. Remove some files to continue.`;
    } else if (exceedsFolderLimit) {
        subtext = `This batch would put you ${foldersOverBy} over the room's ${FOLDER_LIMIT}-folder limit. Remove some files to continue.`;
    } else if (exceedsStorage) {
        subtext = `This batch would put your room ${formatBytes(storageOverBy)} over the ${formatBytes(ROOM_STORAGE_LIMIT_BYTES)} storage limit. Remove some files to continue.`;
    } else {
        subtext = mode === 'folder' ? 'Review this folder before uploading.' : 'Review this upload before uploading.';
    }

    return (
        <div className="new-snip file-limit-popup fade-in" style={{ zIndex: 9999 }}>
            <div className="ns-top">
                <h3>{mode === 'folder' ? 'Review Folder Upload' : 'Review Upload'}</h3>

                <button className="ns-cancel" onClick={onCancel} aria-label="Close">
                    <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                        <g id="Layer_3" data-name="Layer 3">
                            <path
                                className="cls-1"
                                d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                transform="translate(-124.17 -181.62)"
                            />
                        </g>
                    </svg>
                </button>
            </div>

            <p className="flp-subtext">{subtext}</p>

            <div id="confirm-upload-previews">
                {currentFiles.map((file, index) => {
                    const url = previewUrls[index];
                    const displayName = mode === 'folder' && file.webkitRelativePath
                        ? file.webkitRelativePath
                        : file.name;

                    return (
                        <div className="file-preview-card relative" key={`${displayName}-${file.lastModified}-${index}`}>
                            <button
                                style={{ zIndex: 9999 }}
                                className="nf-cancel"
                                onClick={() => removeFileAt(index)}
                                aria-label={`Remove ${file.name}`}
                            >
                                <svg className="x-icon-f" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                                    <g id="Layer_3" data-name="Layer 3">
                                        <path
                                            className="cls-1"
                                            d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                            transform="translate(-124.17 -181.62)"
                                        />
                                    </g>
                                </svg>
                            </button>

                            <div className="preview-icon">
                                {url ? (
                                    <img className="file-preview-thumb" src={url} alt={file.name} />
                                ) : (
                                    <img draggable={false} src={getPreviewIconSrc(file)} alt={file.name} />
                                )}
                            </div>

                            <div>
                                <span className="f-prev-name" title={displayName}>{shortenFileName(displayName)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="f-p-btn-c">
                <div className="quick-flex" style={{ gap: '10px' }}>
                    <span id="fts">{totalSizeLabel} &middot; {currentFiles.length} item{currentFiles.length === 1 ? '' : 's'}</span>

                    {exceedsItemLimit ? (
                        <p className="issue">File Limit Exceeded &mdash; remove {itemsOverBy} more</p>
                    ) : exceedsFolderLimit ? (
                        <p className="issue">Folder Limit Exceeded &mdash; remove {foldersOverBy} more folder{foldersOverBy === 1 ? '' : 's'}</p>
                    ) : exceedsStorage ? (
                        <p className="issue">{formatBytes(storageOverBy)} over the {formatBytes(ROOM_STORAGE_LIMIT_BYTES)} limit</p>
                    ) : (
                        <p></p>
                    )}
                </div>

                <div className="quick-flex" style={{ gap: '10px' }}>
                    <button className="ns-cancel-b" onClick={onCancel}>Cancel</button>
                    <button
                        className={`ns-create ${!canConfirm ? 'ns-disabled' : ''}`}
                        disabled={!canConfirm}
                        onClick={() => onConfirm(currentFiles)}
                    >
                        Upload {currentFiles.length}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FileLimitPopup
